import { Injectable } from '@angular/core';
import { Platform } from '@ionic/angular';
import { CapacitorSQLite, SQLiteConnection, SQLiteDBConnection } from '@capacitor-community/sqlite';
import { Queue } from '../../classes/queue/queue';
import { Assignment } from '../../models/assignment';
import { AssignmentDetails } from '../../models/assignment-details';
import { DateService } from '../date/date.service';
import { LogService } from '../log/log.service';
import { Tables } from './sql.tables';

@Injectable({
  providedIn: 'root'
})
export class SqlService {
  // Sqlite settings
  private sqlite: SQLiteConnection;
  private db: SQLiteDBConnection | null = null;
  private dbName = 'brains_app.db';

  // Debug helper
  private logTag = 'SqlService';

  constructor(private platform: Platform, private logService: LogService, private dateService: DateService) {
    this.sqlite = new SQLiteConnection(CapacitorSQLite);
  }

  /**
   * Initialize local SQLiteDB.
   */
  public async init(): Promise<void> {
    await this.platform.ready();

    try {
      // Create connection
      this.db = await this.sqlite.createConnection(this.dbName, false, 'no-encryption', 1, false);
      await this.db.open();

      // Create tables
      await this.db.execute(
        `CREATE TABLE IF NOT EXISTS ${Tables.assignment}
         (
           id
           PRIMARY
           KEY,
           date
           TEXT,
           data
           TEXT
         )`
      );
      await this.db.execute(
        `CREATE TABLE IF NOT EXISTS ${Tables.assignmentDetails}
         (
           id
           PRIMARY
           KEY,
           data
           TEXT
         )`
      );
      await this.db.execute(
        `CREATE TABLE IF NOT EXISTS ${Tables.queue}
         (
           id
           PRIMARY
           KEY,
           queue
           TEXT
         )`
      );
    } catch (err) {
      this.logService.error(this.logTag, 'init error');
      this.logService.object(err);
    }
  }

  /**
   * Get assignments from SQLiteDB.
   */
  public async getAssignments(startDate: Date): Promise<Assignment[] | undefined> {
    const startDateString = this.dateService.getDateAsString(startDate);
    try {
      const res = await this.db?.query(
        `SELECT *
         FROM ${Tables.assignment}
         WHERE date = ?`,
        [startDateString]
      );
      if (res && res.values) {
        return res.values.map((row) => JSON.parse(row.data));
      }
      return undefined;
    } catch (err) {
      this.logService.error(this.logTag, 'getAssignments error');
      this.logService.object(err);
      throw err;
    }
  }

  public async getAssignmentById(assignmentId: number): Promise<Assignment> {
    const res = await this.db?.query(
      `SELECT *
       FROM ${Tables.assignment}
       WHERE id = ?`,
      [assignmentId]
    );
    const item = res?.values?.[0];
    if (item) {
      return JSON.parse(item.data);
    }
    throw new Error('Not Found');
  }

  /**
   * Writes an assignment, replacing the stored one when the id already exists.
   */
  public async saveAssignment(assignment: Assignment): Promise<any> {
    const data = JSON.stringify(assignment);
    const startDate = this.dateService.getDateAsString(assignment.startDate);
    return this.db?.run(
      `INSERT INTO ${Tables.assignment} (id, date, data)
       VALUES (?, ?, ?)
       ON CONFLICT(id) DO UPDATE SET date = excluded.date, data = excluded.data`,
      [assignment.id, startDate, data]
    );
  }

  public async updateAssignment(assignment: Assignment): Promise<any> {
    const data = JSON.stringify(assignment);
    return this.db?.run(
      `UPDATE ${Tables.assignment}
       SET data = ?
       WHERE id = ?`,
      [data, assignment.id]
    );
  }

  public async deleteAssignment(assignmentId: number): Promise<any> {
    return this.db?.run(
      `DELETE
       FROM ${Tables.assignment}
       WHERE id = ?`,
      [assignmentId]
    );
  }

  public async getAssignmentDetailsById(assignmentId: number): Promise<AssignmentDetails> {
    const res = await this.db?.query(
      `SELECT *
       FROM ${Tables.assignmentDetails}
       WHERE id = ?`,
      [assignmentId]
    );
    const item = res?.values?.[0];
    if (item) {
      return JSON.parse(item.data);
    }
    throw new Error('Not Found');
  }

  /**
   * Writes assignment details, replacing the stored ones when the id already exists.
   */
  public async saveAssignmentsDetails(assignmentDetails: AssignmentDetails): Promise<any> {
    const data = JSON.stringify(assignmentDetails);
    return this.db?.run(
      `INSERT INTO ${Tables.assignmentDetails} (id, data)
       VALUES (?, ?)
       ON CONFLICT(id) DO UPDATE SET data = excluded.data`,
      [assignmentDetails.id, data]
    );
  }

  public async deleteAssignmentsDetails(assignmentId: number): Promise<any> {
    return this.db?.run(
      `DELETE
       FROM ${Tables.assignmentDetails}
       WHERE id = ?`,
      [assignmentId]
    );
  }

  public async getQueueById(queueId: string): Promise<Queue> {
    const res = await this.db?.query(
      `SELECT *
       FROM ${Tables.queue}
       WHERE id = ?`,
      [queueId]
    );
    const item = res?.values?.[0];
    if (item) {
      return JSON.parse(item.queue);
    }
    throw new Error('Not Found');
  }

  /**
   * Writes the queue, replacing the stored one if the id already exists.
   * An upsert is used because the queue is saved after every change, so a plain
   * insert hits the primary key constraint on all but the very first save.
   */
  public async saveQueue(queueId: string, queue: Queue): Promise<any> {
    const data = JSON.stringify(queue);
    return this.db?.run(
      `INSERT INTO ${Tables.queue} (id, queue)
       VALUES (?, ?)
       ON CONFLICT(id) DO UPDATE SET queue = excluded.queue`,
      [queueId, data]
    );
  }

  public async deleteQueue(queueId: string): Promise<any> {
    return this.db?.run(
      `DELETE
       FROM ${Tables.queue}
       WHERE id = ?`,
      [queueId]
    );
  }

  public async deleteAllData(): Promise<void> {
    const allTableNames = Object.values(Tables);
    for (const table of allTableNames) {
      await this.dropTable(table);
    }
    await this.init();
  }

  private async dropTable(tableName: string): Promise<any> {
    return this.db?.execute(`DROP TABLE IF EXISTS ${tableName}`);
  }
}
